// Firebase init for the play-first web app.
//
// Mirrors admin-web/src/firebase.js (compat API) so call sites read the
// same across the two web codebases. Two things this file does that
// admin-web does NOT, both load-bearing for anonymous play:
//
//   1. App Check (reCAPTCHA v3). Enforcement is live server-side since
//      2026-05-23 for Firestore + Cloud Functions. Without an App Check
//      token EVERY Firestore read/write 401s. activate() must run before
//      any db/auth call — it does, because this module is imported first.
//
//   2. Anonymous auth. The whole play-first thesis: a recipient answers a
//      question before signing up. `signInAnonymously` gets a stable UID
//      that `linkWithCredential` later upgrades in place (same UID, all
//      progress kept) — that upgrade is P3.
//
// The firebaseConfig below is the SAME public client config admin-web
// uses (project predict-6). Client config is not a secret; security is
// enforced by Firestore rules + App Check, not by hiding these values.
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import 'firebase/compat/firestore';
import 'firebase/compat/functions';
import 'firebase/compat/app-check';
import 'firebase/compat/messaging';
import 'firebase/compat/analytics';

const firebaseConfig = {
  apiKey: 'AIzaSyBhSw7UB3r4eL7l5N7FvMr9d6tMtSQnk-k',
  authDomain: 'predict-6.firebaseapp.com',
  projectId: 'predict-6',
  storageBucket: 'predict-6.firebasestorage.app',
  messagingSenderId: '605159055668',
  // The dedicated "Predict Six Web" Firebase Web App (registered 2026-06-13),
  // distinct from admin-web's app so the two clients are separable in
  // analytics + App Check.
  appId: '1:605159055668:web:71cdce29ef925087948561',
  measurementId: 'G-193KSKCCWX',
};

firebase.initializeApp(firebaseConfig);

// ── App Check ──────────────────────────────────────────────────────────
// Activate BEFORE any Firestore/Functions usage. If the site key is
// missing (e.g. a dev who hasn't filled .env.local), we skip activation
// and log loudly rather than crash — but note that calls will then 401
// against the enforced prod backend. There's no silent-success path.
// reCAPTCHA v3 site key for the "Predict Six Web" app (registered in App Check
// 2026-06-13; domains: play.predictsixapp.com + localhost). Site keys are
// PUBLIC by design — they ship in client JS, same secrecy class as the Firebase
// apiKey above — so it's safe to commit. Baked in as the default so the prod
// build always has it regardless of where it runs; an env override is honored.
// The matching SECRET key lives only in Firebase App Check, never here.
const recaptchaSiteKey =
  import.meta.env.VITE_RECAPTCHA_V3_SITE_KEY ||
  '6Le6RB0tAAAAALJ_SmUVqm03MnNjLTqE8_kBDoRh';
const appCheckDebugToken = import.meta.env.VITE_APPCHECK_DEBUG_TOKEN;

export let appCheckActive = false;
// Resolves once the FIRST reCAPTCHA-v3 App Check token has been minted (or has
// failed). Await this before the first App-Check-protected backend call so a
// cold token fetch can't reject it. Defaults to an already-resolved promise so
// callers never hang when App Check is off (dev without a key).
export let appCheckReady = Promise.resolve();

if (import.meta.env.DEV && appCheckDebugToken) {
  // Must be set before activate() so the SDK uses the debug provider in
  // local dev (avoids needing a real reCAPTCHA challenge on localhost).
  // eslint-disable-next-line no-self-assign
  self.FIREBASE_APPCHECK_DEBUG_TOKEN = appCheckDebugToken;
}

if (recaptchaSiteKey) {
  try {
    // compat: activate(reCaptchaV3SiteKeyString, isTokenAutoRefreshEnabled)
    firebase.appCheck().activate(recaptchaSiteKey, true);
    appCheckActive = true;
    // Pre-warm the token at startup. On most screens the first Firestore read
    // warms it incidentally, but the /i/<id> invite route's first action is a
    // CALLABLE (getInvite) with NO prior reads — so without this, a brand-new
    // visitor eats the cold reCAPTCHA mint on that call and it can reject into the
    // "Invite unavailable" dead-end (Tom 2026-06-21). getToken() kicks the mint
    // off now; JoinConfirmation awaits appCheckReady before getInvite.
    appCheckReady = firebase
      .appCheck()
      .getToken()
      .then(() => undefined)
      .catch((e) => {
        console.warn('[app-web] App Check token pre-warm failed:', e);
      });
  } catch (e) {
    console.error('[app-web] App Check activate() failed:', e);
  }
} else {
  console.warn(
    '[app-web] VITE_RECAPTCHA_V3_SITE_KEY is not set. App Check is NOT ' +
      'active — Firestore/Functions calls will 401 against prod ' +
      '(enforcement live since 2026-05-23). Set the key in .env.local.'
  );
}

export const auth = firebase.auth();
// Pin LOCAL persistence explicitly AND export the awaited promise as a barrier
// the session bootstrap waits on. setPersistence is ASYNC: if signInAnonymously()
// (SessionContext) races ahead of it, the session can land in IN-MEMORY (NONE)
// storage, which dies on the next reload — the user "has to log in again." The
// compat SDK defaults to LOCAL, but the race + a silent in-memory fallback when
// IndexedDB is unavailable (some privacy modes / blocked-storage desktop
// profiles) are exactly the forced-re-login symptom. So: (a) await this before
// any sign-in/restore, and (b) on reject fall back to SESSION (survives reloads
// within the tab) rather than the SDK's silent NONE. Never rejects (audit B12).
//
// TWO ADDITIONS (2026-08-13, an Android user reporting he signs in on EVERY
// visit). The barrier below was already right; what was missing is that both
// of its failure modes were INVISIBLE in production — a console.error nobody
// can read on someone else's phone — so a fleet-wide storage problem and one
// person's browser setting look identical from here.
//
//   1. ASK FOR PERSISTENT STORAGE. Chrome on Android evicts a site's
//      IndexedDB under storage pressure unless the origin is marked
//      persistent, and an evicted `firebase:authUser` IS this symptom: the
//      restore finds nothing, the app mints a fresh anonymous session, and a
//      saved account looks logged out. `navigator.storage.persist()` is the
//      documented request for exemption; Chrome grants it on engagement or
//      installation and simply resolves false otherwise. Costs one call and
//      cannot regress anything.
//   2. REPORT THE FALLBACK. `persistence_degraded` fires when LOCAL fails —
//      which is what happens inside an in-app webview (a link opened from
//      Messages / Facebook / Instagram, where storage is isolated and often
//      ephemeral) and in private modes. For a share-driven product that is a
//      LIKELY arrival path, not an edge case, and it would explain "every
//      visit" exactly. Now it is countable instead of anecdotal.
export const authReady = auth
  .setPersistence(firebase.auth.Auth.Persistence.LOCAL)
  .then(() => {
    requestPersistentStorage();
  })
  .catch(async (e) => {
    console.error(
      '[app-web] setPersistence(LOCAL) failed — falling back to SESSION so the ' +
        'session at least survives reloads within this tab:',
      e
    );
    reportPersistenceDegraded('local_failed', e);
    try {
      await auth.setPersistence(firebase.auth.Auth.Persistence.SESSION);
    } catch (e2) {
      console.error('[app-web] setPersistence(SESSION) also failed:', e2);
      reportPersistenceDegraded('session_failed', e2);
    }
  });

/** Ask the browser not to evict our storage. Best-effort and never throws:
 *  unsupported browsers have no `navigator.storage.persist`, and a denial is a
 *  resolved `false`, not an error. The result is reported once so a fleet-wide
 *  denial rate is visible — if most Android sessions are non-persistent, the
 *  eviction theory stops being a theory. */
function requestPersistentStorage() {
  try {
    if (!navigator.storage || typeof navigator.storage.persist !== 'function') return;
    navigator.storage
      .persist()
      .then((granted) => {
        if (!granted) reportPersistenceDegraded('storage_not_persisted', null);
      })
      .catch(() => {});
  } catch (_) {
    /* never let a diagnostic break the boot */
  }
}

/** One analytics line for a session that will not survive the visit. Imported
 *  lazily off the module's own export to avoid a load-order cycle — this file
 *  defines `logWebEvent` further down. */
function reportPersistenceDegraded(reason, err) {
  try {
    logWebEvent('web_persistence_degraded', {
      reason,
      code: err?.code || null,
      // Cheap webview tell: in-app browsers are the arrival path most likely
      // to have isolated, ephemeral storage for a link-shared product.
      standalone: !!(window.matchMedia && window.matchMedia('(display-mode: standalone)').matches),
    });
  } catch (_) {
    /* diagnostics never break the app */
  }
}
export const db = firebase.firestore();
export const functions = firebase.functions();

// Callable Cloud Functions used by the web client (WP3-C coin ladder + badge).
// All best-effort + idempotent server-side, so callers can swallow failures.
export const fn = {
  claimWebFirstSetBonus: functions.httpsCallable('claimWebFirstSetBonus'),
  // NOTE: the +180 app-download bonus is claimed by the iOS app on first launch
  // (App Attest scopes it to the real app) — never from web. No client binding
  // here on purpose; see Play.jsx "Get the app" copy (audit C8/X3).
  // markPwaInstalled — the WEB-side conversion marker (distinct from the iOS
  // download bonus above): stamps users/{uid}.pwaInstalledAt when this device
  // installs the PWA / Add-to-Home-Screen. No coins; idempotent server-side.
  // Fired from util/pwaInstall.js recordPwaInstall() on the appinstalled event +
  // first standalone launch. The Android/desktop analog of appDownloadBonusAt;
  // the admin Web Play tile counts it as "Added to Home Screen".
  markPwaInstalled: functions.httpsCallable('markPwaInstalled'),
  markResultsViewed: functions.httpsCallable('markResultsViewed'),
  // (claimQuestionShareBonus binding removed 2026-07-01 — the +6-per-share
  // economy was retired; shares now create games, rewarded by the +180 referral.)
  // Web first-submission bonus ballot (founder twist — the auth-gated
  // 12-question ballot that REPLACES the iOS group-invite Day-1 step).
  //   getWebBonusBallot   → { claimed, questions:[{id,text,...}] } (server-picked
  //                          cohort; `claimed` true once the one-shot gate is set)
  //   claimWebBonusBallot { answers:[{questionId, answer?, isSkipped?}] }
  //                       → { awarded, written } (server re-validates each id
  //                          against its own picker, writes votes, stamps gate)
  // Both require a SECURED (non-anonymous) caller — anonymous sessions are
  // rejected (failed-precondition), since the whole point is the auth gate.
  getWebBonusBallot: functions.httpsCallable('getWebBonusBallot'),
  claimWebBonusBallot: functions.httpsCallable('claimWebBonusBallot'),
  registerWebPushToken: functions.httpsCallable('registerWebPushToken'),
  unregisterWebPushToken: functions.httpsCallable('unregisterWebPushToken'),
  // Back Sections §3f visibility: `{} → { visible }`. Web has no Remote Config
  // and config/sections is admin-only-readable, so the public half of iOS's
  // `sections_enabled || sectionsFounder` gate is simply not derivable here.
  // The server answers with `sectionsVisibleFor` — the one shared truth.
  getSectionsGate: functions.httpsCallable('getSectionsGate'),
  // Web invite/join loop (the "FLIP"). Server functions are platform-agnostic;
  // graduation (founders step aside) auto-fires inside performGroupJoin when the
  // joined group's kind === "founder". No new server / rules code.
  createOrGetGroupInvite: functions.httpsCallable('createOrGetGroupInvite'),
  // Pending head-to-head invite — mints NO group; the 2-person game is born
  // when a friend accepts (server create-on-accept). "Play a Friend" entry point.
  createH2HInvite: functions.httpsCallable('createH2HInvite'),
  // Pending-invites row: list the caller's not-yet-accepted h2h invites, and
  // cancel (revoke) one before anyone accepts.
  listPendingH2HInvites: functions.httpsCallable('listPendingH2HInvites'),
  cancelH2HInvite: functions.httpsCallable('cancelH2HInvite'),
  getInvite: functions.httpsCallable('getInvite'),
  acceptInvite: functions.httpsCallable('acceptInvite'),
  // Leave a group (non-creator). Server-side because firestore.rules only lets
  // the creator mutate the group doc — a member can't self-remove client-side.
  // Creators delete the group instead (deleteGroup, direct write). Input { groupId }.
  leaveGroup: functions.httpsCallable('leaveGroup'),
  // Public leaderboard (global top-100 + the caller's rank/neighbors). Already
  // deployed + used by iOS; web reuses it. Input { range?: 'month' | 'accuracy' }
  // — the two boards iOS shows. ('all' is the server's legacy points board, kept
  // only for pre-retirement iOS clients; web must never send it.)
  getPublicLeaderboard: functions.httpsCallable('getPublicLeaderboard'),
  // Creator-growth verdict share cards (CG-A3) — tonight's per-member results,
  // aggregated server-side because a client can't read another player's answers
  // subcollection (firestore.rules), so the per-question tonight's-correct is
  // only computable with admin credentials.
  getGroupDailyResults: functions.httpsCallable('getGroupDailyResults'),
  getVersusDailyResults: functions.httpsCallable('getVersusDailyResults'),
  // Past settled weeks of an H2H rivalry — the Rivalry tab's fixture list. The
  // record + the live week ride the group doc, so this is the only weekly-H2H
  // read that needs a round-trip (docs/weekly-h2h-proposal.md §7.1).
  getH2HHistory: functions.httpsCallable('getH2HHistory'),
  // Propose / accept / cancel the standing weekly wager. amount:null cancels.
  setH2HWager: functions.httpsCallable('setH2HWager'),
  // The caller's own latest reveal { hasResults, correct, total } — backs the
  // creator content kit's "Tonight's card" (CG-B / follow_creator_night).
  getMyDailyReveal: functions.httpsCallable('getMyDailyReveal'),
  //   getLeagueRecap {} → { recap } — the creator's latest settled League week.
  //   Self-only; null before the first settlement.
  getLeagueRecap: functions.httpsCallable('getLeagueRecap'),
  // CG-C: send a fellow fan of the same creator a challenge REQUEST (server
  // picks the opponent). { creatorUid } → { requested, requestId?, opponentName?,
  // reason? }. No game exists until they accept via respondToFanMatch.
  matchWithFan: functions.httpsCallable('matchWithFan'),
  // The opponent accepts/declines a fan-match request. { requestId, accept } →
  // { status: "accepted"|"declined"|"expired"|"gone", groupId? }.
  respondToFanMatch: functions.httpsCallable('respondToFanMatch'),
  // Safety: report + block a matched opponent. { groupId, opponentUid } →
  // { success }. Files a report, blocks re-matching, and removes them from the game.
  reportAndBlockOpponent: functions.httpsCallable('reportAndBlockOpponent'),
  // Manage-blocks: list the players you've blocked + undo a block.
  getMyBlockedPlayers: functions.httpsCallable('getMyBlockedPlayers'),
  unblockPlayer: functions.httpsCallable('unblockPlayer'),
  // Creator Autopilot per-type switches. {} reads the current map; { set: {...} }
  // merges the given switches. Always returns { settings } (every key default ON).
  creatorAutopilotSettings: functions.httpsCallable('creatorAutopilotSettings'),
  // Latest posted tweet from @predictsixapp (for the Profile "Follow on X"
  // card). Read-only; returns { post: { text, url, type, postedAt } | null }.
  getLatestXPost: functions.httpsCallable('getLatestXPost'),
  // Live founder-ambassador (Tom/Phil/Cathy) profiles — overlaid onto their
  // First Group rows because their membersSnapshot entries freeze at group
  // creation (skipMembersSnapshotSync). See data/groups.js applyFounderFreshness.
  getFounderProfiles: functions.httpsCallable('getFounderProfiles'),
  // Permanent account deletion (caller's own account). Already deployed for
  // iOS; web calls it then signs out → a fresh anonymous session starts.
  deleteUserAccount: functions.httpsCallable('deleteUserAccount'),
  // Cosmetics shop (already deployed for iOS; platform-agnostic). Purchase
  // debits coins + writes users/{uid}/inventory/{itemId}; equip/unequip set
  // users/{uid}.equippedCosmetics.{category} (NOT client-writable directly, so
  // these MUST go through the callables). Inputs: { itemId, category } /
  // { category }. See data/cosmeticsCatalog.js for the web catalog mirror.
  purchaseCosmeticItem: functions.httpsCallable('purchaseCosmeticItem'),
  equipCosmeticItem: functions.httpsCallable('equipCosmeticItem'),
  unequipCosmeticItem: functions.httpsCallable('unequipCosmeticItem'),
  // Group chat (already deployed for iOS; platform-agnostic). Posts a
  // user_message card to groups/{gid}/feed; the server gates membership,
  // founder-group read-only, and mute, and awards +2 coins on the first post
  // per ET day in a 2+ member group. Input { groupId, body } (web v1 omits
  // mentions/GIF/reply). Returns { success, posted, feedCardId, coinsAwarded }.
  postUserFeedMessage: functions.httpsCallable('postUserFeedMessage'),
  // Toggle one of the 6 allowed reactions on a feed card. Server-side write
  // via the Admin SDK (feed docs are `allow write: if false` in
  // firestore.rules — clients NEVER write reactions directly), so no rules
  // change is needed. Server gates membership + mute, validates the emoji
  // against FEED_REACTION_EMOJIS, and toggles the caller's uid in/out of
  // reactions.<emoji>. Input { groupId, cardId, emoji } → returns
  // { success, added, reactions } where `reactions` is the canonical
  // post-write map (emoji → uid[]); an empty array === nobody reacted.
  toggleFeedReaction: functions.httpsCallable('toggleFeedReaction'),
  // Head-to-head challenges (already deployed for iOS; platform-agnostic).
  // Coins debit only at acceptChallenge (both sides), winner gets 2x; decline/
  // cancel move no coins. Server re-validates membership / disagreement / coins
  // / opponent opt-in on every call. Web v1 omits counter + stake-update.
  //   createChallenge  { groupId, questionId, opponentUid, stake } → { success, challengeId }
  //   acceptChallenge / declineChallenge / cancelChallenge  { challengeId } → { success }
  //   getGroupChallengeEligibility { groupId } → { success, eligibility: {uid:{acceptsChallenges,coinBalance}} }
  createChallenge: functions.httpsCallable('createChallenge'),
  acceptChallenge: functions.httpsCallable('acceptChallenge'),
  declineChallenge: functions.httpsCallable('declineChallenge'),
  cancelChallenge: functions.httpsCallable('cancelChallenge'),
  getGroupChallengeEligibility: functions.httpsCallable('getGroupChallengeEligibility'),
  // Group-creator moderation (Phase 5; new Cloud Functions). Creator-only,
  // server-enforced; can't target self or a seeded founder. kick atomically
  // removes the member from the denormalized snapshot; mute toggles
  // groups/{gid}.mutedMemberIds (enforced by postUserFeedMessage).
  //   kickGroupMember     { groupId, targetUid } → { success }
  //   setGroupMemberMuted { groupId, targetUid, muted } → { success, muted }
  kickGroupMember: functions.httpsCallable('kickGroupMember'),
  setGroupMemberMuted: functions.httpsCallable('setGroupMemberMuted'),
  // Streak Saver (already deployed for iOS; platform-agnostic). Takes NO args —
  // the server re-validates eligibility (currentStreak, per-tier rescue window,
  // 1/ET-month gate, payment path founder/intro-free/coins) inside its
  // transaction and writes only already-whitelisted user-doc fields, so the web
  // StreakSaverModal's eligibility/cost math is DISPLAY-ONLY (no rules change).
  // Returns { success, cost, savedStreak, tier, newBalance, paymentPath }.
  saveStreak: functions.httpsCallable('saveStreak'),
  // Double-down + change-answer parity (web, shipped 2026-07-08). Both are
  // platform-agnostic — no iOS-only gating server-side (functions/src/index.ts).
  //   changeAnswer      { questionId, newAnswer } → { success, noChange, cancelledChallengeCount, chargedCoins }
  //     30 coins (free for a founding member's first change of the ET day),
  //     once per question per day, window open through 2 AM ET the day after
  //     the question's publishDate.
  //   changeDoubleDown  { questionId } → { success, noChange }
  //     Free, once per ET day — moves the user's 2x pick to `questionId`.
  changeAnswer: functions.httpsCallable('changeAnswer'),
  changeDoubleDown: functions.httpsCallable('changeDoubleDown'),
  // Follow / Creator mode (docs/follow-mode-proposal.md). One-way follow +
  // compare against an approved creator — no chat, no challenge. The web /f
  // landing calls resolveFollowLink to render the creator, then followCreator
  // on confirm (idempotent server-side). Both platform-agnostic.
  //   resolveFollowLink { handle } → { creator, alreadyFollowing }
  //   followCreator     { handle | creatorUid, source } → { success, creator }
  //   revealPredictorAnswer { questionId, handle } → { answered, predictorAnswered?, predictorAnswer?, agreed?, isSelf? }
  //     Server-enforced: it will not say until the CALLER's own answer exists.
  resolveFollowLink: functions.httpsCallable('resolveFollowLink'),
  revealPredictorAnswer: functions.httpsCallable('revealPredictorAnswer'),
  followCreator: functions.httpsCallable('followCreator'),
  unfollowCreator: functions.httpsCallable('unfollowCreator'),
  // Follow & Compare consumption (parity with iOS follow face).
  //   getFollowedCreators {} → { creators: [...] }
  //   listCreators { query } → { creators: [...] }  (Discover browse/search)
  //   getCreatorFollowers { creatorUid, range } → { top, totalFollowers, callerRank }
  getFollowedCreators: functions.httpsCallable('getFollowedCreators'),
  listCreators: functions.httpsCallable('listCreators'),
  getCreatorFollowers: functions.httpsCallable('getCreatorFollowers'),
  // Creator Updates (broadcasts + one reaction per viewer).
  //   listCreatorBroadcasts { creatorUid } → { posts: [...] }
  //   reactToCreatorBroadcast { creatorUid, broadcastId, emoji } → { ok }
  //   postCreatorBroadcast { text } → { ok }  (self only)
  listCreatorBroadcasts: functions.httpsCallable('listCreatorBroadcasts'),
  reactToCreatorBroadcast: functions.httpsCallable('reactToCreatorBroadcast'),
  postCreatorBroadcast: functions.httpsCallable('postCreatorBroadcast'),
  // CG-E safety: report an objectionable creator broadcast. { broadcastId } → { success }.
  reportCreatorBroadcast: functions.httpsCallable('reportCreatorBroadcast'),
  // Apply to become a creator from web (parity with the iOS Profile opt-in).
  //   applyForCreator { requestedHandle, xHandle, note } → { success, status, handle, xHandle, verifyCode }
  //   submitCreatorVerification { postUrl } → { success }
  //   getMyCreatorVerification {} → { exists, status, requestedHandle, xHandle, verifyCode, verifyCodeExpired, verifyPostUrl }
  applyForCreator: functions.httpsCallable('applyForCreator'),
  submitCreatorVerification: functions.httpsCallable('submitCreatorVerification'),
  getMyCreatorVerification: functions.httpsCallable('getMyCreatorVerification'),
  //   setCreatorProfile { bio, socials, website } → { success, bio, socials, website }
  setCreatorProfile: functions.httpsCallable('setCreatorProfile'),
  // Change the creator's public @handle. Rejects a taken handle with a friendly
  // already-exists error. Case-insensitive: follow links keep resolving.
  //   setCreatorHandle { handle } → { success, handle }
  setCreatorHandle: functions.httpsCallable('setCreatorHandle'),
  // Suggest a "[creator] asks" question (F3). Creators write the question; the
  // Predict Six team assigns a category at review. Text-only (no category).
  //   submitCreatorQuestion { text } → { success, id }
  submitCreatorQuestion: functions.httpsCallable('submitCreatorQuestion'),
};

// Re-export the namespace so call sites can reach FieldValue / Timestamp
// / serverTimestamp without a second import.
export { firebase };
export const FieldValue = firebase.firestore.FieldValue;
export const Timestamp = firebase.firestore.Timestamp;

// ── Analytics (web → GA4 → BigQuery) ───────────────────────────────────────
// Closes the "web leg is invisible" gap (see
// docs/web-play-vs-ios-metrics-strategy.md): app-web previously fired ZERO
// analytics events, so web behavior never reached BigQuery. Initialized guarded
// so SSR / unsupported / ad-blocked environments no-op rather than throw.
// `measurementId` is set in firebaseConfig above.
let _analytics = null;
try {
  if (typeof window !== 'undefined' && firebaseConfig.measurementId) {
    _analytics = firebase.analytics();
  }
} catch (e) {
  console.warn('[app-web] analytics init skipped:', (e && e.message) || e);
}

/**
 * Fire a web analytics event (best-effort; no-ops if analytics is unavailable).
 * Names use a `web_` prefix so the web funnel is trivially separable from iOS
 * in GA4 / BigQuery. Never throws — instrumentation must not break the UI.
 */
export function logWebEvent(name, params) {
  try {
    if (_analytics) _analytics.logEvent(name, params || {});
  } catch {
    // swallow — analytics is best-effort
  }
}

/**
 * Sign in anonymously if not already signed in. Resolves to the current
 * user. Throws `auth/operation-not-allowed` if Anonymous auth is not yet
 * enabled in the Firebase Console — callers surface that as a friendly
 * "not available yet" state rather than a crash.
 */
export async function ensureAnonymousSession() {
  if (auth.currentUser) return auth.currentUser;
  const cred = await auth.signInAnonymously();
  return cred.user;
}

// ── Web push (WP4-C) ─────────────────────────────────────────────────────
// The PUBLIC Web Push VAPID key for the "Predict Six Web" app (generated in
// Console → Cloud Messaging → Web Push certificates, 2026-06-13). Used only
// client-side in getToken(); it's public by design (ships in client JS), like
// the reCAPTCHA site key + apiKey above. The matching private key lives only
// in Firebase.
const VAPID_KEY =
  'BIUERTi3JQG8ExNqdzHGFuRo4in6HCcJ20tc47KHIrjLJRGq8GC8M23YtOcaPZcyq6aX0j0LlUx50mJvN_F8_0I';

/** Cheap synchronous check — enough to decide whether to SHOW the opt-in. */
export function webPushMaybeSupported() {
  return (
    typeof window !== 'undefined' &&
    'Notification' in window &&
    'serviceWorker' in navigator &&
    'PushManager' in window
  );
}

/**
 * Turn on web push for the (secured) current user. Requests permission,
 * registers the dedicated FCM service worker at its own scope (so it doesn't
 * replace the PWA SW at `/`), fetches an FCM token, and registers it +
 * the browser timezone server-side via the `registerWebPushToken` callable.
 *
 * Returns { ok: true } on success, or { ok:false, reason } where reason is
 * 'unsupported' | 'denied' | 'default' | 'no-token' | 'error'.
 */
export async function enableWebPush() {
  try {
    if (!webPushMaybeSupported()) return { ok: false, reason: 'unsupported' };
    const supported = await firebase.messaging.isSupported();
    if (!supported) return { ok: false, reason: 'unsupported' };

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return { ok: false, reason: permission };

    // Dedicated scope keeps this separate from the PWA SW (/sw.js at `/`).
    const swReg = await navigator.serviceWorker.register(
      '/firebase-messaging-sw.js',
      { scope: '/firebase-push/' }
    );
    const messaging = firebase.messaging();
    const token = await messaging.getToken({
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: swReg,
    });
    if (!token) return { ok: false, reason: 'no-token' };

    const timezone =
      (typeof Intl !== 'undefined' &&
        Intl.DateTimeFormat().resolvedOptions().timeZone) ||
      '';
    await fn.registerWebPushToken({ token, timezone });
    localStorage.removeItem('ps_web_push_off');
    return { ok: true };
  } catch (e) {
    console.error('[app-web] enableWebPush failed:', e);
    return { ok: false, reason: 'error' };
  }
}

/**
 * Turn OFF web push: delete the FCM token client-side and clear it server-side
 * (`unregisterWebPushToken`), so the retention crons stop targeting this
 * browser. We can't revoke the browser's Notification permission from JS — only
 * the user can, in their browser settings — so we also set a local "off" flag
 * (`ps_web_push_off`) the opt-in UI reads to show the re-enable button even
 * though `Notification.permission` is still 'granted'. Best-effort: the server
 * clear is what actually stops pushes, so we set the flag + return ok even if
 * the client-side deleteToken throws (e.g. SW already gone).
 *
 * Returns { ok: true } once the server token is cleared, else { ok:false }.
 */
export async function disableWebPush() {
  try {
    try {
      const messaging = firebase.messaging();
      await messaging.deleteToken();
    } catch (e) {
      // Non-fatal — the server-side clear below is the source of truth.
      console.warn('[app-web] deleteToken failed (continuing):', e);
    }
    await fn.unregisterWebPushToken();
    localStorage.setItem('ps_web_push_off', '1');
    return { ok: true };
  } catch (e) {
    console.error('[app-web] disableWebPush failed:', e);
    return { ok: false, reason: 'error' };
  }
}
